import {
  ComponentPropsWithoutRef,
  ElementType,
  forwardRef,
  PropsWithChildren,
  Ref,
} from 'react';

import { cn } from '../../../helpers/classBuilders';

const BASE_CLASSNAME = 'c-Paragraph';

/**
 * Font size, on Tailwind's own scale including the line-height each stock
 * utility bundles. The `--p-size*` tokens this replaces are 12/14/16/18/20 for
 * every brand (and 24 by fallback for `2xl`, which no theme defines), so each
 * step lands exactly.
 */
const sizeClasses = {
  xs: 'text-xs',
  sm: 'text-sm',
  md: 'text-base',
  lg: 'text-lg',
  xl: 'text-xl',
  '2xl': 'text-2xl',
} as const;

const colorClasses = {
  default: 'text-gray-600',
  light: 'text-gray-500',
  strong: 'text-gray-900',
  white: 'text-white',
} as const;

const alignClasses = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
} as const;

/**
 * The base weight is `--ft-weight-normal`, which is **500** — `font-medium`,
 * not `font-normal`.
 */
const weightClasses = {
  regular: 'font-medium',
  bolder: 'font-bold',
} as const;

interface ParagraphOwnProps {
  className?: string;
  variant?: 'base' | 'error';
  /**
   * Semantic text color. `default` = gray-600 (body), `light` = gray-500
   * (captions/labels/dates), `strong` = gray-900 (emphasis), `white` = text on
   * a dark surface (tooltips, gradient cards). Prefer this prop over
   * `text-gray-*!` / `text-white!` overrides. The `error` variant still wins
   * over any color (it is a semantic state, not a shade).
   */
  color?: keyof typeof colorClasses;
  isBolder?: boolean;
  align?: keyof typeof alignClasses;
  size?: keyof typeof sizeClasses;
}

type NoChildrenElements =
  | 'img'
  | 'input'
  | 'br'
  | 'hr'
  | 'meta'
  | 'link'
  | 'source'
  | 'track'
  | 'area'
  | 'base'
  | 'col'
  | 'embed'
  | 'param'
  | 'wbr';

type ElementTypeWithChildren = Exclude<ElementType, NoChildrenElements>;

// Define the props for the forwardRef component
type ParagraphProps<T extends ElementTypeWithChildren> = PropsWithChildren<
  {
    tag?: T;
  } & ParagraphOwnProps &
    ComponentPropsWithoutRef<T>
>;

// Use forwardRef to wrap your component
export const Paragraph = forwardRef(
  <T extends ElementTypeWithChildren>(
    props: ParagraphProps<T>,
    ref: Ref<T>,
  ) => {
    const {
      tag = 'p',
      children,
      className: propClassName = '',
      variant = 'base',
      color = 'default',
      isBolder = false,
      align = 'left',
      size = 'md',
      ...rest
    } = props;

    const Component = tag as T;

    /**
     * Ordering is the API contract, not a formatting detail:
     *
     * - `c-Paragraph` carries no styles. It stays as a selector hook — the
     *   Developers page styles `.c-Paragraph ol li` and friends.
     * - The margin/padding reset goes *before* `className`, so spacing stays
     *   the consumer's.
     * - Typography goes *after* `className`, so tailwind-merge drops a plain
     *   `text-blue-500` or `text-sm`. Use the `color`, `size` and `align`
     *   props; an important class still wins for a deliberate exception.
     * - The error colour is merged after the palette colour, because it is a
     *   semantic state rather than a shade.
     */
    const finalClassName = cn(
      BASE_CLASSNAME,
      'm-0 p-0',
      propClassName,
      'font-(family-name:--font-stack)',
      weightClasses[isBolder ? 'bolder' : 'regular'],
      alignClasses[align],
      sizeClasses[size],
      colorClasses[color],
      variant === 'error' && 'text-red-900',
    );

    return (
      <Component
        className={finalClassName}
        ref={ref}
        {...(rest as any)} // TODO - To fix
      >
        {children}
      </Component>
    );
  },
) as <T extends ElementTypeWithChildren>(
  props: ParagraphProps<T> & { ref?: Ref<T> },
) => React.ReactElement;
